import React, { useState, useEffect } from "react";
import {
  Button,
  Row,
  Col,
  Card,
  Form,
  Container,
  FormControl,
  Table,
  Modal,
  Alert,
} from "react-bootstrap";
import PerfectScrollbar from "react-perfect-scrollbar";
import { db, auth } from "../firebaseConfig";
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  query,
  where,
  doc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";

export default function CreateInvoices() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [newInvoice, setNewInvoice] = useState({
    items: [],
    additionalCosts: 0,
    vatRate: 12.5,
    notes: "",
  });
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedInvoiceForModal, setSelectedInvoiceForModal] = useState(null);

  // Fetch user role and permissions
  const fetchUserRoleAndPermissions = async (uid) => {
    try {
      const userDocRef = doc(db, "Users", uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const role = userDoc.data().role;
        setUserRole(role);

        if (role === "admin") {
          setPermissions({ canCreateInvoices: true }); // Admin always has access
          setAccessDenied(false);
          return;
        }

        const roleDocRef = doc(db, "Roles", role);
        const roleDoc = await getDoc(roleDocRef);

        if (roleDoc.exists()) {
          const rolePermissions = roleDoc.data().permissions;
          setPermissions(rolePermissions);

          if (!rolePermissions.canCreateInvoices) {
            setAccessDenied(true);
          } else {
            setAccessDenied(false);
          }
        } else {
          console.error("Role document not found.");
          setAccessDenied(true);
        }
      } else {
        console.error("User document not found.");
        setAccessDenied(true);
      }
    } catch (error) {
      console.error("Error fetching user role and permissions:", error);
      setAccessDenied(true);
    }
  };

  // Fetch users
  const fetchAllUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, "Users"));
      const userList = usersSnapshot.docs.map((doc) => ({
        email: doc.data().email,
        fullName: doc.data().fullName || "Unknown User",
      }));
      setUsers(userList);
      setFilteredUsers(userList);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  // Fetch invoices
  const fetchInvoices = async (userEmail = null) => {
    try {
      const invoicesRef = collection(db, "Invoices");
      const invoicesQuery = userEmail
        ? query(invoicesRef, where("user.email", "==", userEmail))
        : invoicesRef;
      const invoicesSnapshot = await getDocs(invoicesQuery);

      const invoiceList = invoicesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setInvoices(invoiceList);
    } catch (err) {
      console.error("Error fetching invoices:", err);
    }
  };

  // Add a new item to the invoice
  const addItem = () => {
    setNewInvoice((prev) => ({
      ...prev,
      items: [...prev.items, { name: "", description: "", unitCost: 0, quantity: 1, cost: 0 }],
    }));
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setNewInvoice({
      items: invoice.items,
      additionalCosts: invoice.additionalCosts,
      vatRate: invoice.vatRate,
      notes: invoice.notes,
    });
    setSelectedUser(invoice.user);
  };

  // Open modal with selected invoice details
  const handleModalOpen = (invoice) => {
    setSelectedInvoiceForModal(invoice);
    setShowModal(true);
  };


  // Handle changes to invoice items
  const handleItemChange = (index, field, value) => {
    setNewInvoice((prev) => {
      const updatedItems = [...prev.items];
      updatedItems[index][field] = value;

      if (field === "unitCost" || field === "quantity") {
        updatedItems[index].cost = Number(updatedItems[index].unitCost) * Number(updatedItems[index].quantity);
      }

      return { ...prev, items: updatedItems };
    });
  };

  // Save invoice
  const saveInvoice = async () => {
    if (!selectedUser) {
      alert("Please select a user for the invoice.");
      return;
    }

    if (newInvoice.items.length === 0) {
      alert("Invoice must have at least one item.");
      return;
    }

    try {
      const subTotal = newInvoice.items.reduce((sum, item) => sum + item.cost, 0);
      const vat = (subTotal * newInvoice.vatRate) / 100;
      const totalCost = subTotal + vat + parseFloat(newInvoice.additionalCosts || 0);

      const invoiceData = {
        user: selectedUser,
        items: newInvoice.items,
        additionalCosts: newInvoice.additionalCosts,
        vatRate: newInvoice.vatRate,
        notes: newInvoice.notes,
        totalCost,
        createdAt: new Date(),
      };

      if (editingInvoice) {
        const invoiceRef = doc(db, "Invoices", editingInvoice.id);
        await updateDoc(invoiceRef, invoiceData);
        alert("Invoice updated successfully!");
        setEditingInvoice(null);
      } else {
        const invoicesRef = collection(db, "Invoices");
        await addDoc(invoicesRef, invoiceData);
        alert("Invoice created successfully!");
      }

      setNewInvoice({ items: [], additionalCosts: 0, vatRate: 12.5, notes: "" });
      fetchInvoices(selectedUser.email);
    } catch (err) {
      console.error("Error saving invoice:", err);
      alert("Failed to save invoice.");
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    const lowerCaseQuery = query.toLowerCase();
    const filtered = users.filter(
      (user) =>
        user.fullName.toLowerCase().includes(lowerCaseQuery) ||
        user.email.toLowerCase().includes(lowerCaseQuery)
    );
    setFilteredUsers(filtered);
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    fetchInvoices(user.email);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        setUser(authUser);
        await fetchUserRoleAndPermissions(authUser.uid);
        fetchAllUsers();
      } else {
        setUser(null);
        setPermissions(null);
        setAccessDenied(true);
        setFilteredUsers([]);
        setInvoices([]);
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <React.Fragment>
      <Header />
      <Container fluid className="main main-app p-4">
        {accessDenied ? (
          <Alert variant="danger">
            Access Denied: You do not have permission to create invoices.
          </Alert>
        ) : (
          <>
            <h4 className="mb-4 text-primary">Create and Manage Invoices</h4>

            <Card className="filter-card mb-4 shadow-sm">
              <Card.Body>
                <h5 className="mb-3 text-primary">Select User</h5>
                <FormControl
                  type="text"
                  placeholder="Search employees..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="mb-3"
                />
                <PerfectScrollbar style={{ maxHeight: "200px", overflow: "auto" }}>
                  <ul className="list-group">
                    {filteredUsers.map((user, index) => (
                      <li
                        key={index}
                        className={`list-group-item ${selectedUser === user ? "active" : ""}`}
                        onClick={() => handleUserSelect(user)}
                        style={{ cursor: "pointer" }}
                      >
                        {user.fullName} ({user.email})
                      </li>
                    ))}
                  </ul>
                </PerfectScrollbar>
              </Card.Body>
            </Card>

            {selectedUser && (
              <>
                <Card className="invoice-card mb-4 shadow-sm">
                  <Card.Body>
                    <h5 className="mb-3 text-primary">Invoice Details</h5>
                    <Table bordered>
                      <thead>
                        <tr>
                          <th>Item Name</th>
                          <th>Description</th>
                          <th>Unit Cost</th>
                          <th>Quantity</th>
                          <th>Cost</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {newInvoice.items.map((item, index) => (
                          <tr key={index}>
                            <td>
                              <Form.Control
                                type="text"
                                value={item.name}
                                onChange={(e) => handleItemChange(index, "name", e.target.value)}
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="text"
                                value={item.description}
                                onChange={(e) => handleItemChange(index, "description", e.target.value)}
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                value={item.unitCost}
                                onChange={(e) => handleItemChange(index, "unitCost", e.target.value)}
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                              />
                            </td>
                            <td>${item.cost.toFixed(2)}</td>
                            <td>
                              <Button
                                variant="danger"
                                onClick={() =>
                                  setNewInvoice((prev) => ({
                                    ...prev,
                                    items: prev.items.filter((_, i) => i !== index),
                                  }))
                                }
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    <Button variant="success" onClick={addItem} className="mb-3">
                      Add Item
                    </Button>
                    <Form.Group>
                      <Form.Label>Additional Costs</Form.Label>
                      <Form.Control
                        type="number"
                        value={newInvoice.additionalCosts}
                        onChange={(e) =>
                          setNewInvoice((prev) => ({ ...prev, additionalCosts: parseFloat(e.target.value) }))
                        }
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>VAT Rate (%)</Form.Label>
                      <Form.Control
                        type="number"
                        value={newInvoice.vatRate}
                        onChange={(e) =>
                          setNewInvoice((prev) => ({ ...prev, vatRate: parseFloat(e.target.value) }))
                        }
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Notes</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={newInvoice.notes}
                        onChange={(e) =>
                          setNewInvoice((prev) => ({ ...prev, notes: e.target.value }))
                        }
                      />
                    </Form.Group>
                    <Button variant="primary" onClick={saveInvoice} className="mt-3">
                      {editingInvoice ? "Update Invoice" : "Save Invoice"}
                    </Button>
                  </Card.Body>
                </Card>

                <h5 className="mb-4 text-primary">Existing Invoices</h5>
                <Table bordered>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Invoice ID</th>
                      <th>Created At</th>
                      <th>Total Cost</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{invoice.id}</td>
                        <td>{new Date(invoice.createdAt.seconds * 1000).toLocaleDateString()}</td>
                        <td>${invoice.totalCost.toFixed(2)}</td>
                        <td>{invoice.notes}</td>
                        <td>
                          <Button variant="info" onClick={() => handleModalOpen(invoice)}>
                            View
                          </Button>{" "}
                          <Button variant="warning" onClick={() => handleEdit(invoice)}>
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </>
        )}

        {/* Modal for Viewing Invoice */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Invoice Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedInvoiceForModal && (
              <>
                <h5>Invoice ID: {selectedInvoiceForModal.id}</h5>
                <p>
                  <b>Created At:</b>{" "}
                  {new Date(selectedInvoiceForModal.createdAt.seconds * 1000).toLocaleDateString()}
                </p>
                <p>
                  <b>Notes:</b> {selectedInvoiceForModal.notes || "No additional notes provided."}
                </p>
                <Table bordered>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Item Name</th>
                      <th>Description</th>
                      <th>Quantity</th>
                      <th>Unit Cost</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoiceForModal.items.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{item.name}</td>
                        <td>{item.description}</td>
                        <td>{item.quantity}</td>
                        <td>${Number(item.unitCost).toFixed(2)}</td>
                        <td>${(Number(item.unitCost) * Number(item.quantity)).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
      <Footer />
    </React.Fragment>
  );
}
